// File: src/utils/liverc.js
// Reading LiveRC's published results.
//
// LiveRC gives every track its own subdomain (gandsraceway.liverc.com) and puts
// every result behind one script with a `p` parameter. The importer is handed a
// single event URL and works everything else out from the page itself:
//
//   /results/?p=view_event&id=515151            the event, and links to:
//     /results/?p=event_overall_ranking&id=515151   "Final Results"
//     /results/?p=view_points&id=7718580            "Results (best 1 of 3)"
//
// Note the second id. The points page has its own id space, so its URL cannot be
// composed from the event id - it has to be read off the event page, which is
// why the importer fetches that first rather than going straight to the results.
//
// The parsing lives here rather than in the component because this is the part
// that breaks when LiveRC redesigns, and a pure function is the part that can be
// tested against saved markup.

const LIVERC_DOMAIN = 'liverc.com';

// Exactly the check the server-side proxy makes. A bare endsWith would also
// accept evilliverc.com.
const isLiveRCHost = (hostname) =>
  hostname === LIVERC_DOMAIN || hostname.endsWith(`.${LIVERC_DOMAIN}`);

// Pages whose `id` is the EVENT id. view_points is deliberately absent: its id
// belongs to another table, and accepting it would quietly import a different
// event's results.
const EVENT_PAGES = ['view_event', 'event_overall_ranking'];

const normaliseText = (value) =>
  (value || '')
    .replace(/ /g, ' ') // NBSP -> space
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Pull the track origin and the event id out of a pasted LiveRC URL.
 *
 * Returns { origin, eventId } or { error }, where error is a translation key so
 * the dialog can say which part was wrong rather than "invalid URL".
 */
export const parseLiveRCUrl = (input) => {
  const raw = (input || '').trim();
  if (!raw) return { error: 'livercUrlRequired' };

  let url;
  try {
    url = new URL(raw);
  } catch {
    return { error: 'livercNotALiveRCUrl' };
  }

  if (!isLiveRCHost(url.hostname)) return { error: 'livercNotALiveRCUrl' };

  const page = url.searchParams.get('p');
  if (page && !EVENT_PAGES.includes(page)) return { error: 'livercNotAnEventUrl' };

  const id = url.searchParams.get('id');
  if (!id || !/^\d+$/.test(id)) return { error: 'livercNoEventId' };

  return { origin: url.origin, eventId: id };
};

export const buildLiveRCEventUrl = ({ origin, eventId }) =>
  `${origin}/results/?p=view_event&id=${eventId}`;

// A link read off a fetched page, resolved and checked. The proxy will fetch
// anything under liverc.com, so a link that leaves the track we were pointed at
// is refused rather than followed.
const sameOriginLink = (anchor, origin) => {
  if (!anchor) return null;
  try {
    const resolved = new URL(anchor.getAttribute('href'), origin);
    return resolved.origin === origin ? resolved.toString() : null;
  } catch {
    return null;
  }
};

/**
 * The event page: what it is called, and where its two result pages are.
 *
 * Returns { trackName, eventName, eventDate, finalResultsUrl, pointsUrl }.
 * `pointsUrl` is null for an event with no qualifying rounds, which is normal
 * for a club night.
 */
export const parseLiveRCEventPage = (doc, origin) => {
  const headings = Array.from(doc.querySelectorAll('.page-header'))
    .map(node => normaliseText(node.textContent))
    .filter(Boolean);

  return {
    // h1 is the track, h3 the event, h5 the date. Read by position among the
    // page headers because none of them carries a distinguishing class.
    trackName: headings[0] || '',
    eventName: headings[1] || '',
    eventDate: headings[2] || '',
    finalResultsUrl: sameOriginLink(
      doc.querySelector('a[href*="p=event_overall_ranking"]'), origin),
    pointsUrl: sameOriginLink(
      doc.querySelector('a[href*="p=view_points"]'), origin)
  };
};

// Which column holds the position, and which the driver.
//
// Three LiveRC pages share this parser and they do not agree on the header:
// the overall ranking says "Pos" and carries empty Brand and Country columns
// before the driver, the points page says "#". Anything else is positional
// guesswork, so fall back to the first two columns only as a last resort.
const RANK_HEADER = /^(pos\.?|#)$/i;
const DRIVER_HEADER = /^driver$/i;

const findColumns = (table) => {
  const headerRows = Array.from(table.querySelectorAll('thead tr'));
  const headers = headerRows.length
    ? Array.from(headerRows[headerRows.length - 1].querySelectorAll('th'))
    : [];

  let rank = -1;
  let driver = -1;

  headers.forEach((header, index) => {
    const label = normaliseText(header.textContent);
    if (rank === -1 && RANK_HEADER.test(label)) rank = index;
    if (driver === -1 && DRIVER_HEADER.test(label)) driver = index;
  });

  return { rank: rank === -1 ? 0 : rank, driver: driver === -1 ? 1 : driver };
};

/**
 * Every class on a LiveRC results page, in the order the page lists them.
 *
 * Returns [{ className, rows: [{ rank, driverName }] }], or null when the
 * document holds no ranking table at all - so the caller can say "no results
 * table" rather than "no drivers", which are different problems.
 */
export const parseLiveRCRankingTables = (doc) => {
  const tables = Array.from(doc.querySelectorAll('table.round_rankings, table.leaderboard_points'));
  if (tables.length === 0) return null;

  return tables.map((table) => {
    const columns = findColumns(table);
    const heading = table.querySelector('.class_header');
    const rows = [];

    for (const row of Array.from(table.querySelectorAll('tbody tr'))) {
      const cells = Array.from(row.querySelectorAll('td'));
      // Spanning rows are separators, not results.
      if (cells.length === 0 || cells[0].hasAttribute('colspan')) continue;
      if (cells.length <= Math.max(columns.rank, columns.driver)) continue;

      const rank = parseInt(normaliseText(cells[columns.rank].textContent), 10);
      const driverName = normaliseText(cells[columns.driver].textContent);

      // A purely numeric name means the wrong column was located. Dropping the
      // row beats importing a lap count as a person, which is what the MyRCM
      // parser learned the hard way.
      if (Number.isNaN(rank) || !driverName || /^[\d/:.]+$/.test(driverName)) continue;

      rows.push({ rank, driverName });
    }

    return { className: normaliseText(heading && heading.textContent), rows };
  });
};
